import { Link } from "@react-navigation/native";
import {
    Button,
    Form,
    DataTable,
    Loading,
    Picker,
    Spacer,
    StyleFunction,
    useThemedStyle,
    Alert,
} from "@venuepos/react-common";
import { AvailableLocale, useTranslation } from "locales";
import React from "react";
import produce from "immer";
import {
    GQCashRegistersQuery,
    GQFiskalyClientsQuery,
    GQFiskalyEstablishmentsQuery,
} from "graphql-sdk";
import { FiskalyEstablishmentMappingType } from "../fiskaly-form";
import { View } from "react-native";
import { useAdminSession } from "../../../../session";
import { formatRelativeDate } from "lib";

export function FiskalyEstablishmentMappingForm({
    form,
    establishments,
    cashRegisters,
    clients,
    onSubmit,
}: {
    form: Form<FiskalyEstablishmentMappingType>;
    establishments: GQFiskalyEstablishmentsQuery["fiskalyEstablishments"];
    cashRegisters: GQCashRegistersQuery["cashRegisters"];
    clients: GQFiskalyClientsQuery["fiskalyClients"];
    onSubmit: () => void;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ locale }] = useAdminSession(["locale"]);
    const [{ values }, { setValue }] = form;

    if (
        !values?.establishmentMap ||
        !establishments ||
        !clients ||
        !cashRegisters
    ) {
        return <Loading />;
    }

    return (
        <>
            <DataTable>
                <DataTable.Header>
                    <DataTable.Title>
                        {t("backoffice.common.cash_register", "Cash Register")}
                    </DataTable.Title>
                    <DataTable.Title>
                        {t(
                            "backoffice.integration.fiskaly.establishment",
                            "Establishment"
                        )}
                    </DataTable.Title>
                    <DataTable.Title numeric>
                        {t("backoffice.common.updated_at", "Updated at")}
                    </DataTable.Title>
                </DataTable.Header>
                {cashRegisters.data?.map((registerItr, idx) => {
                    const foundClient = values.establishmentMap.find(
                        mapItr => mapItr.cashRegisterId === registerItr.id
                    );

                    const client = clients?.data.find(
                        clientItr => clientItr.id === foundClient?.clientId
                    );

                    return (
                        <DataTable.Row
                            key={`establishment-mapping-row_${idx}`}
                            style={idx % 2 === 0 ? styles.oddRow : null}
                        >
                            <DataTable.Cell>
                                <Link
                                    to={{
                                        screen: "CASH_REGISTER_EDIT",
                                        params: { id: registerItr.id },
                                    }}
                                    style={styles.link}
                                >
                                    {registerItr.name}
                                </Link>
                            </DataTable.Cell>
                            <DataTable.Cell>
                                <Picker
                                    style={styles.picker}
                                    selectedValue={foundClient?.establishmentId}
                                    onValueChange={newValue =>
                                        setValue(
                                            "establishmentMap",
                                            produce(
                                                values.establishmentMap,
                                                draft => {
                                                    const index =
                                                        draft.findIndex(
                                                            itr =>
                                                                itr.cashRegisterId ===
                                                                registerItr.id
                                                        );

                                                    if (index !== -1) {
                                                        draft[
                                                            index
                                                        ].establishmentId =
                                                            newValue;
                                                    } else {
                                                        draft.push({
                                                            clientId:
                                                                foundClient.clientId,
                                                            cashRegisterId:
                                                                registerItr.id,
                                                            establishmentId:
                                                                newValue,
                                                        });
                                                    }
                                                }
                                            )
                                        )
                                    }
                                    disabled={!foundClient}
                                >
                                    {foundClient ? (
                                        <Picker.Item
                                            label={t(
                                                "backoffice.common.select",
                                                "Select an item"
                                            )}
                                            value=""
                                            key="__select__"
                                        />
                                    ) : (
                                        <Picker.Item
                                            label={t(
                                                "backoffice.integration.fiskaly.client_unregistered",
                                                "Unregistered"
                                            )}
                                            value=""
                                            key="__register__"
                                        />
                                    )}
                                    {establishments?.data.map(
                                        establishmentItr => {
                                            const label =
                                                establishmentItr.remarks
                                                    ? `${establishmentItr.designation}: ${establishmentItr.remarks}`
                                                    : establishmentItr.designation;

                                            return (
                                                <Picker.Item
                                                    label={label}
                                                    value={establishmentItr.id}
                                                    key={establishmentItr.id}
                                                />
                                            );
                                        }
                                    )}
                                </Picker>
                            </DataTable.Cell>
                            <DataTable.Cell numeric>
                                {client
                                    ? formatRelativeDate(
                                          client?.updatedAt,
                                          locale as AvailableLocale
                                      )
                                    : null}
                            </DataTable.Cell>
                        </DataTable.Row>
                    );
                })}
                {establishments.data.length === 0 ? (
                    <Alert type="info" style={styles.bottomSpace}>
                        {t(
                            "backoffice.integration.fiskaly.mapping_no_establishments",
                            "No establishments found yet. Create some in the tab 'Establishments'."
                        )}
                    </Alert>
                ) : null}
                {cashRegisters.data.length === 0 ? (
                    <Alert type="info">
                        {t(
                            "backoffice.integration.fiskaly.mapping_no_cashregisters",
                            "No cash registers found for this mapping. Go to Cash Registers in Backoffice and register them with Fiskaly."
                        )}
                    </Alert>
                ) : null}
            </DataTable>
            <Spacer />
            <View style={styles.row}>
                <Button
                    onPress={onSubmit}
                    disabled={
                        (cashRegisters && cashRegisters.data.length === 0) ||
                        (establishments && establishments.data.length === 0)
                    }
                    size="small"
                >
                    {t("backoffice.common.save", "Save")}
                </Button>
            </View>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    picker: {
        marginVertical: 0,
        marginBottom: 0,
    },
    bottomSpace: {
        marginBottom: theme.spacingScale,
    },
    link: {
        ...theme.styles.link,
    },

    row: {
        ...theme.styles.row,
    },
    oddRow: {
        ...theme.styles.oddRow,
    },
});
